// components/WorkshopLayout.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WorkshopSidebar from './WorkshopSidebar';
import { Outlet, useLocation } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import ProfileMenu from './ProfileMenu';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import VehicleSearchBar from './VehicleSearchBar';

const WorkshopLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { token, user, logout } = useAuth();
  const { pathname } = useLocation();
  // Pages converted to the chrome/ink design own their max-width and padding
  // (via components/ui/Iparc <Page>), so they render edge-to-edge against the
  // sidebar. Anything not yet converted keeps the original gutter. Add a route
  // here when its page is converted.
  const FULL_BLEED = [
    '/workshop',
    '/workshop/requests',
    '/workshop/walk-in',
    '/workshop/reception-checklist',
    '/workshop/log-maintenance',
    '/workshop/catalog',
    '/workshop/quotations',
    '/workshop/invoices',
    '/workshop/receipts',
    '/workshop/delivery-notes',
    '/workshop/purchase-orders',
    '/workshop/ledger',
    '/workshop/finance',
    '/workshop/categories',
    '/workshop/profile',
    // Both use <Page>, which owns its own width and padding.
    '/workshop/parts-request',
    '/workshop/supplier-quotations',
  ];
  const isFullBleed = FULL_BLEED.includes(pathname);
  // Sidebar badges: lifted here so the sidebar and the dashboard read the same
  // numbers. Without this the badges sit at 0 and contradict the dashboard.
  const [badges, setBadges] = useState({
    pendingQuotes: 0, unpaidInvoices: 0, supplierQuotes: 0, vehicleCount: undefined,
  });

  useEffect(() => {
    if (!token || !user?._id) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API_URL}/workshop-quotations/summary`, h).catch(() => null),
      axios.get(`${API_URL}/workshop-invoices/summary`, h).catch(() => null),
      axios.get(`${API_URL}/vehicles/assigned/${user._id}`, h).catch(() => null),
      axios.get(`${API_URL}/quotations/supplier-quotations/owner`, h).catch(() => null),
    ]).then(([q, i, v, s]) => {
      // Supplier devis still waiting on the workshop — the same "needs a
      // decision" set the page counts, so the badge and the page agree.
      const quotes = Array.isArray(s?.data) ? s.data : [];
      setBadges({
        pendingQuotes: Number(q?.data?.pendingQuotations) || 0,
        unpaidInvoices: Number(i?.data?.unpaidInvoices) || 0,
        supplierQuotes: quotes.filter(
          (r) => r?.status === 'submitted' || r?.status === 'supplier_countered'
        ).length,
        vehicleCount: Array.isArray(v?.data) ? v.data.length : undefined,
      });
    });
  }, [token, user]);

  // Notifications polling
  useEffect(() => {
    if (!token) return;
    const fetchNotifs = () =>
      axios
        .get(`${API_URL}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setNotifications(res.data))
        .catch(() => {});
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 15000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <div className="flex w-full min-h-screen bg-[#0D0C0B] text-[#F4F1EA] font-sans">
      {/* Desktop Sidebar */}
      <div className="hidden sm:block">
        <WorkshopSidebar {...badges} onLogout={logout} />
      </div>

      {/* Mobile Sidebar (slide-over) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex sm:hidden">
          <div className="relative w-64 bg-gray-900 text-white">
            <button
              className="absolute top-2 right-2 p-2 text-white"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              ✖
            </button>
            <WorkshopSidebar {...badges} onLogout={logout} />
          </div>
          {/* Backdrop */}
          <div
            className="flex-1 bg-black bg-opacity-40"
            onClick={() => setSidebarOpen(false)}
          />
        </div>
      )}

      {/* Main content area */}
      <main
        className={`flex-1 min-w-0 overflow-y-auto ${
          isFullBleed ? 'p-0' : 'p-4 sm:p-6'
        }`}
      >
        {/* Top bar: mobile menu + right-side icons */}
        <div className={`flex items-center justify-between mb-4 ${isFullBleed ? "px-[30px] pt-6" : ""}`}>
          {/* Mobile hamburger */}
          <div className="sm:hidden flex items-center">
            <button
              className="p-2 rounded bg-gray-800 text-white"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="ml-3 text-lg font-bold">Workshop</span>
          </div>
          {/* Spacer for desktop (hamburger hidden) */}
          <div className="hidden sm:block" />
          {/* Search, Profile, notifications, language — always visible */}
          <div className="flex items-center gap-3">
            <VehicleSearchBar />
            <LanguageSwitcher />
            <NotificationBell
              notifications={notifications}
              onRefresh={() =>
                axios
                  .get(`${API_URL}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
                  .then((res) => setNotifications(res.data))
                  .catch(() => {})
              }
            />
            <ProfileMenu user={user} />
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
};

export default WorkshopLayout;
